'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { usePublicUserProfileQuery } from 'codegen/generated/user'
import { useSocialUserFollowersInfiniteQuery } from '../_hooks/use-followers-infinite'
import { UserListSection } from '../_components/user-list'
import { formatUserName } from 'utils/format-user-name'

export function FollowersContent({ userId, slug }: { userId: string; slug: string }) {
  const t = useTranslations('social-feed.page.crafter_profile')
  const user = useUser()

  const { data: publicUserProfile } = usePublicUserProfileQuery(
    { slug },
    { select: d => d?.publicUserProfile, enabled: Boolean(slug) },
  )

  const fullUserName = formatUserName(
    publicUserProfile?.firstName,
    publicUserProfile?.lastName,
    'User',
  )
  const isOwn = user.data?.slug === slug

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useSocialUserFollowersInfiniteQuery(userId)

  const following = React.useMemo(
    () => data?.pages.flatMap(p => p?.userFollowers?.followers ?? []) ?? [],
    [data],
  )

  React.useEffect(() => {
    if (!isLoading && following.length === 0 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [isLoading, following.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <UserListSection
      users={following}
      hasMore={hasNextPage}
      isFetchingMore={isFetchingNextPage}
      isLoading={isLoading}
      fetchMore={fetchNextPage}
      emptyMessage={
        isOwn ? t('empty_following') : t('empty_user_following', { user: fullUserName })
      }
      ownUserId={user.data?.id}
      slug={slug}
    />
  )
}
